import { Inject, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { Observable, firstValueFrom, map, of, tap } from 'rxjs';
import { LoginRequest } from '../models/security/login-request';
import { LoginResponse } from '../models/security/login-response';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { PermissionResponse } from '../models/security/permission-response';
import { RegisterRequest } from '../models/security/register-request';
import { HttpUtil } from '../utils/http-util';
import { isPlatformBrowser } from '@angular/common';
import { Response } from '../models/shared/response';
import { AddUserResponse } from '../models/security/add-user-response';
import { SIGNAL } from '@angular/core/primitives/signals';
import { UserRole } from '../models/security/user-role-enum';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly _tokenKey = 'token';
  private permissionCodes = signal<Map<string, PermissionResponse>>(new Map<string, PermissionResponse>());
  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _toastService: ToastrService = inject(ToastrService);
  private readonly _router: Router = inject(Router);
  public isLoggedIn = signal(this.isValidToken());

  public redirectUrl: string = '';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  public getToken(): string | null {
    return isPlatformBrowser(this.platformId) ? localStorage.getItem(this._tokenKey) : null;
  }

  public getUserName(): string | null {
    let token = this.getToken();

    if (token == null) {
      return null;
    }

    const decodedToken: any = jwtDecode(token);

    if (!decodedToken) {
      return null;
    }

    return decodedToken.name;
  }

  public getUserEmail(): string | null {
    let token = this.getToken();

    if (token == null) {
      return null;
    }

    const decodedToken: any = jwtDecode(token);

    if (!decodedToken) {
      return null;
    }

    return decodedToken.email;
  }

  public getUserRoleId(): string | null {
    let token = this.getToken();

    if (token == null) {
      return null;
    }

    const decodedToken: any = jwtDecode(token);

    if (!decodedToken) {
      return null;
    }

    return decodedToken.profile;
  }

  public getUserId(): number | null {
    let token = this.getToken();

    if (token == null) {
      return null;
    }

    const decodedToken: any = jwtDecode(token);

    if (!decodedToken) {
      return null;
    }

    return decodedToken.sub ? +decodedToken.sub : null;
  }

  public isValidToken(): boolean {
    let token = this.getToken();

    if (token == null) {
      return false;
    }

    const decodedToken: any = jwtDecode(token);

    if (!decodedToken) {
      return false;
    }

    const expirationDate = decodedToken.exp * 1000;
    const currentDate = new Date().getTime();
    return expirationDate > currentDate;
  }

  private getRouteByRole(role: string | undefined | null) {

    let route: string = '';

    if (this.redirectUrl) {
      route = this.redirectUrl;
      this.redirectUrl = '';
    } else {
      switch(role){
        case UserRole.Member:
          route = '/candidates/main'
          break;
        case UserRole.Company:
          route = '/companies/main'
          break;
        case UserRole.Admin:
          route = '/admin/main'
          break;
        default:
          route = '/home'
          break;
      }
    }

    return route;
  }

  doLoginNavigate(){
    // Verificar si el email está verificado antes de navegar a la ruta correspondiente al rol
    if (!this.isEmailVerified()) {
      this._router.navigate(['/email-verification']);
      return;
    }
    
    const role = this.getUserRoleId();
    const toRoute = this.getRouteByRole(role);
    this._router.navigate([toRoute]);
  }

  public login(request: LoginRequest): Observable<any> {
    return this._http.post<Response<LoginResponse>>(`${environment.api.url}/Auth/Login`, request)
    .pipe(
      tap(response => {
        this.setToken(response?.singleData?.token);
      })
    );
  }

  register(request: RegisterRequest) {
    return this._http.post<AddUserResponse>(`${environment.api.url}/Auth/Register`, request);
  }

  updateAccount(request: RegisterRequest) {
    return this._http.post<AddUserResponse>(`${environment.api.url}/Auth/UpdateAccount`, request);
  }

  userExist(userName: string) {
    return this._http.get<boolean>(`${environment.api.url}/Auth/UserExist`, {
      params: HttpUtil.toHttpParams({
        userName: userName
      })
    })
  }

  emailExist(email: string) {
    return this._http.get<boolean>(`${environment.api.url}/Auth/EmailExist`, {
      params: HttpUtil.toHttpParams({
        email: email
      })
    })
  }

  public setToken(token: string | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    if (token == null) {
      localStorage.removeItem(this._tokenKey);
    } else {
      localStorage.setItem(this._tokenKey, token);
    }
    this.isLoggedIn.set(this.isValidToken());
  }

  public getPermissions(): Observable<PermissionResponse[]> {
    return this._http.get<PermissionResponse[]>(`${environment.api.url}/Auth/My/Permission`);
  }

  public setPermissions(permissions: PermissionResponse[] | null): void {
    if (permissions == null) {
      this.permissionCodes.set(new Map<string, PermissionResponse>())
    } else {
      const permissionsByCode = new Map<string, PermissionResponse>()
      for (let permission of permissions) {
        permissionsByCode.set(permission.code, permission);
      }
      this.permissionCodes.set(permissionsByCode);
    }
  }

  public logout(returnUrl: string | null = null): void {
    this.setToken(null);
    this._toastService.clear();
    this._router.navigateByUrl('/login', {
      state: {
        returnUrl: returnUrl
      }
    });
  }

  public canDoActions(permissionCodes: string[], canDoActions: (actions: boolean[]) => void): void {
    canDoActions(permissionCodes.map(code => this.permissionCodes().has(code)));
  }

  public hasPermission(permissionCode: string) {
    return this.permissionCodes().has(permissionCode);
  }

  public sendConfirmEmail(userName: string): Observable<any> {
    return this._http.post<any>(`${environment.api.url}/Auth/sendConfirmEmail`, {
      userName: userName
    });
  }

  public sendForgotPassword(userName: string): Observable<any> {
    return this._http.post<Response<any>>(`${environment.api.url}/Auth/sendForgotPassword`, {
      userName: userName
    });
  }

  public sendForgotUserName(email: string): Observable<any> {
    return this._http.post<Response<any>>(`${environment.api.url}/Auth/SendForgotUserName`, {
      email: email
    });
  }

  public confirmEmail(request: { userName: string, token: string }): Observable<any> {
    return this._http.post<Response<any>>(`${environment.api.url}/Auth/confirmEmail`, request);
  }

  public forgotPassword(request: { userId: number, token: string, password: string }): Observable<any> {
    return this._http.post<any>(`${environment.api.url}/Auth/forgotPassword`, request);
  }

  public resetPassword(request: { userId: number, password: string }): Observable<any> {
    return this._http.post<any>(`${environment.api.url}/Auth/resetPassword`, request);
  }

  public isEmailVerified(): boolean {
    // Esta función simula una validación local
    // En una implementación real, debería consultarse a la API o almacenar este estado en el token
    // Por ahora, implementaremos una lógica simple para propósitos de demostración
    let token = this.getToken();

    if (token == null) {
      return false;
    }

    const decodedToken: any = jwtDecode(token);

    if (!decodedToken) {
      return false;
    }

    // Asumimos que la información de verificación de email vendría en el token como 'email_verified'
    // Esto debe ajustarse según la estructura real del token en la aplicación
    return decodedToken.email_verified === 'True';
  }

  public checkEmailVerification(): Observable<boolean> {
    // Este método debería consultar a la API si el email está verificado
    // Si la API no provee esta funcionalidad, se puede implementar
    // por ahora usamos un endpoint que debería existir o agregarse en la API
    return this._http.get<boolean>(`${environment.api.url}/Auth/isEmailVerified`);
  }

}
